(ns ductile.ui.service-list
  (:require [clojure.edn :as edn]
            [clojure.set :as set]
            [clojure.string :as str]
            [ductile.time.format :as time-format]
            [ductile.ui.components :as ui]
            [ductile.ui.deframe :as deframe]
            [ductile.ui.fahrzeug :as fahrzeug]
            [ductile.ui.locale :as locale :refer [tr trn]]
            [ductile.ui.mobile :as mobile]
            [ductile.ui.router :as router]
            [ductile.ui.util :as util]
            [nextjournal.clerk.render.localstorage :as localstorage]
            [nextjournal.log :as log]
            [reagent.core :as r]))

(def color-defs
  {:green {:group-bg "bg-green-200 lg:bg-green-100 hover:bg-green-300 "
           :group-expanded-bg "bg-green-200 hover:bg-green-300 "
           :termin-bg "bg-green-200 "}
   :gray {:group-bg "bg-gray-300 lg:bg-gray-200 hover:bg-gray-400 "
          :group-expanded-bg "bg-gray-300 hover:bg-gray-400 "
          :termin-bg "bg-gray-300 "}
   :orange {:group-bg "bg-orange-200 lg:bg-orange-100 hover:bg-orange-300 "
            :group-expanded-bg "bg-orange-200 hover:bg-orange-300 "
            :termin-bg "bg-orange-200 "}
   :red {:group-bg "bg-red-200 lg:bg-red-100 hover:bg-red-300 "
         :group-expanded-bg "bg-red-200 hover:bg-red-300 "
         :termin-bg "bg-red-200 "}})

(defn show-vin [{:vehicle/keys [vin]}]
  [:div.font-mono.text-sm.tracking-wider.group.cursor-pointer
   {:class "lg:w-[200px]"}
   [:span.text-indigo-500.group-hover:underline (subs vin 0 (- (count vin) 7))]
   [:span.font-bold.text-indigo-700.group-hover:underline (subs vin (- (count vin) 7))]])

(defn parked-at-str [{:as fat-fahrzeug :parking-spot/keys [row place]}]
  (str/join "/" (remove nil? [(:parking-zone/name fat-fahrzeug) row place])))

(defn loading-line-str [{:parking-spot/keys [row zone]}]
  (when zone
    (str (:parking-zone/name zone) row)))

(defn vin-link [vin]
  (router/path '[compound-ops :compound-name workflow :fahrzeug-id]
               (assoc (:path-params @deframe/!current-route) :fahrzeug-id vin)))

(defn navigate-to-vin! [vin]
  (deframe/assoc! :previous-url (str (.-pathname (.-location js/window))))
  (router/navigate-to (vin-link vin)))

(defn error [error-value]
  [:div.p-4
   [:div.rounded-lg.bg-red-50.px-6.pt-3.pb-4.max-w-2xl.border.border-2.border-red-600.shadow-xl.text-red-600
    [:div.flex.items-center
     [ui/icon {:size 20} :alert-triangle]
     [:h3.text-lg.text-red-600.mt-0.ml-1 {:style {:margin-top 3}} "Error"]]
    [:div.text-red-600.font-bold error-value]]])

(defn show-error-or [value else-body]
  (if (:error value)
    [error (:error value)]
    (else-body value)))

(defn article-count [entries]
  (str (count entries) " " (trn ["Vehicle" "Vehicles"] (count entries))))

(defn filters-dropdown
  ([{:as opts :keys [list-size options label on-change]}]
   (let [select-opts (-> opts
                         (dissoc :list-size :label :options)
                         (update :value pr-str)
                         (assoc :on-change (fn [^js e] (on-change (edn/read-string (.. e -target -value))))))]
     [:div.text-xs.mb-3.font-bold.flex.items-center.-mt-1.w-full.gap-3
      [:div.flex.items-center.gap-2
       [:span (or label (str (tr "Show") ":"))]
       (into [:select.select-box.w-full
              (merge
               {:style {:font-size 12 :font-weight "bold"}
                :class "py-[8px] md:py-[5px]"}
               select-opts)]
             (map (fn [o] [:option (update o :value pr-str)]))
             options)]
      (when list-size
        [:div.text-gray-500.flex-shrink-0.white-space-nowrap
         {:class "lg:w-[150px]"}
         (str list-size " " (trn ["Vehicle" "Vehicles"] list-size))])])))

(def pdi-color-coding
  {:today       :red
   :tomorrow    :orange
   :next-monday :orange
   :past        :green
   :unknown     :gray
   :future      :green})

(def attr->default-schema
  {:vehicle/model
   {:render (fn [v fahrzeug] [:a.flex.gap-2.items-center v [fahrzeug/package-badge fahrzeug]])}
   :vehicle/vin
   {:render (fn [v] [:a.font-mono.cursor-pointer {:on-click #(navigate-to-vin! v) :href (vin-link v)} [ui/vin v]])}
   :parking-spot
   {:label (tr "Zone / Row / Place")
    :derive (comp (partial remove nil?) (juxt :parking-zone/name :parking-spot/row :parking-spot/place))
    :render (fn [v] [:div.flex.items-center.tabular-nums.font-bold.whitespace-nowrap (into [:<>] (interpose [:span.text-sm.mx-1.text-slate-400 "/"] v))])}
   :fahrzeug/delivery-dealer-code-raw
   {:label (tr "Dealer")
    :render (fn [v] [:span.tabular-nums v])}
   :service-list/service-codes
   {:render (fn [v] (str/join "," v))}

   :fahrzeug/expected-customer-delivery-datetime
   {:render #(time-format/->str {:format :date :locale @locale/!current-locale} %)}

   :transport/preparable-at
   {:render (fn [_ fahrzeug _] (time-format/->str {:format :datetime :locale @locale/!current-locale} (-> fahrzeug :exit/transport :transport/preparable-at)))}

   :transport/loading-line
   {:render (fn [_ fahrzeug _] (-> fahrzeug :exit/transport :transport/loading-line loading-line-str))}

   :load/id
   {:render (fn [_ fahrzeug _] (-> fahrzeug :exit/transport :load/id))}

   :fahrzeug/destination
   {:render (fn [v _ _] (:address/name v))}})

(defn responsive-table [opts data]
  (let [mobile? (ui/use-mobile? 950)
        opts (update opts :schema (partial map (fn [attr+opts]
                                                 (let [[attr opts] (if (keyword? attr+opts) [attr+opts {}] attr+opts)]
                                                   [attr
                                                    (merge
                                                     (attr->default-schema attr)
                                                     opts)]))))]
    [ui/simple-table
     (merge opts {:mobile? mobile?
                  :table (fn [row] (cond-> {:class ["mb-5 w-full rounded-md overflow-hidden ring ring-white ring-2 bg-white shadow-md"
                                                    (when mobile? "text-sm cursor-pointer hover:ring-indigo-400")]}
                                     (and mobile? (:vehicle/vin row))
                                     (assoc :on-click #(navigate-to-vin! (:vehicle/vin row)))))
                  :thead {:class (when-not mobile? "bg-slate-50")}
                  :th (fn [attr]
                        {:class
                         (ui/class-vec
                          "text-left font-normal text-[11px]"
                          (if mobile?
                            "p-1 w-1/3 "
                            ["p-2 border-b uppercase text-gray-500"
                             (case attr
                               :vehicle/vin "w-24"
                               :vehicle/license-plate "w-[10rem]"
                               :unloading-city "w-[10rem]"
                               :fahrzeug/expected-customer-delivery-datetime "w-[9rem]"
                               :service/scheduled-for-datetime "w-[9rem]"
                               :parking-spot/place "w-[11rem]"
                               :transport/loading-line ""
                               :fahrzeug/delivery-dealer-code-raw "w-[8rem]"
                               nil)]))})
                  :tr {:class [(if mobile?
                                 "odd:bg-white even:bg-slate-50"
                                 "hover:bg-indigo-200/20 border-b last:border-b-0")]}
                  :td (fn [v row attr]
                        {:class [(when (and (= :vehicle/vin attr) (not mobile?)) "cursor-pointer text-indigo-700 hover:underline")
                                 (if mobile? "p-1" "p-2")]})})
     data]))

(defn base-service-list [{:as params :keys [data sort-fn display-choices schema statistics]}]
  (show-error-or
   data
   (fn [fahrzeuge]
     (let [display-choice-id @(deframe/!localstorage-get ::display-choice-id (or (first display-choices) :py-priority))
           group-header->expanded? @(deframe/!route-get-in [::group-header->expanded?] {})
           {:keys [display-fn display-kind]} (first (filter (comp #{display-choice-id} :id) display-choices))
           items (->> fahrzeuge
                      (sort-by (or
                                (when (and sort-fn (= :by-priority display-choice-id)) sort-fn)
                                (juxt :parking-zone/name :parking-spot/row :parking-spot/place)))
                      ((or display-fn identity)))]

       (into
        [:div.overflow-y-auto.flex-auto.p-4.bg-slate-100
         [:div.mb-3.w-full
          (into [:select.select-box.mr-4
                 {:style {:font-size 12 :font-weight "bold"}
                  :class "py-[8px] md:py-[5px]"
                  :data-testid (str :display-choice)
                  :value display-choice-id
                  :on-change (fn [event]
                               (let [v (.. event -target -value)]
                                 (deframe/localstorage-assoc! ::display-choice-id (keyword v))))}]
                (map (fn [{:keys [id]}] [:option {:value id :label (tr id)}]))
                (if (seq display-choices)
                  display-choices
                  (map (fn [id] {:id id}) [:by-priority :by-parking-zone])))
          [:div.flex.flex-auto.flex-wrap.gap-2.mt-2
           (when-let [open-stats (:open statistics)]
             [:div.p-2.bg-white.rounded-lg.flex-grow
              [:h6.whitespace-nowrap. (tr "Open Services")]
              (if-let [rows (:stats open-stats)]
                (let [cols (:columns open-stats)]
                  (into [:table {:class ["text-[12px]"]}
                         (into [:tr (for [col cols] [:th.text-left.first:pl-2.pr-4 (str/capitalize (tr col))])])]
                        (for [row rows]
                          (into [:tr (for [col cols]
                                       (let [v (get row col)]
                                         [:td.first:pl-2.pr-4 {:class (when (number? v) "text-right")} v]))]))))
                (tr "None"))])
           (when-let [completed-stats (:completed statistics)]
             [:div.p-2.bg-white.rounded-lg.flex-grow
              [:h6.whitespace-nowrap (case (:timespan completed-stats)
                                       :today (tr "Completed today")
                                       :this-week (tr "Completed this week"))]
              (if-let [rows (:stats completed-stats)]
                (let [cols (:columns completed-stats)]
                  (into [:table {:class ["text-[12px]"]}
                         (into [:tr (for [col cols] [:th.text-left.first:pl-2.pr-4 (str/capitalize (tr col))])])]
                        (for [row rows]
                          (into [:tr (for [col cols]
                                       (let [v (get row col)]
                                         [:td.first:pl-2.pr-4 {:class (when (number? v) "text-right")} v]))]))))
                (tr "None"))])]]

         [responsive-table {:schema schema}
          (cond->> items
            (#{:grouped} display-kind)
            (mapcat (fn [[group-header fahrzeuge]]
                      (let [collapsed? (not (group-header->expanded? group-header))]
                        (into [{:table/colspan-full
                                [:div.bg-indigo-600.hover:bg-indigo-700.active:bg-indigo-800.text-white.text-lg.font-bold.rounded-t-lg.px-3.py-2.flex.justify-between.items-center.select-none.cursor-pointer
                                 {:class "mb-2 min-[950px]:mb-0"
                                  :data-testid (str :table-group-header- (if collapsed? "collapsed" "expanded") "-" group-header)
                                  :on-click #(deframe/route-update-in! [::group-header->expanded? group-header] not)}

                                 [:div.flex.items-center.gap-2
                                  [:div.transition-all
                                   {:class (when collapsed? "-rotate-90")}
                                   [ui/icon {:size 16} :chevron-down]]
                                  group-header]

                                 [:span.text-base.border.border-white.px-2.rounded-full.shadow.flex.items-center.bg-white.bg-opacity-20
                                  {:class "py-[2px]"}
                                  [:span.mr-1 (count fahrzeuge)]
                                  [ui/icon {:size 18} :car]]]}]
                              (map #(assoc % :table/collapsed? collapsed?))
                              fahrzeuge)))))
          items]])))))

(defn navigate-to-workflow! [params]
  (router/navigate-to
   (router/path '[compound-ops :compound-name flow :workflow/type :ductile/id]
                (merge (:path-params @deframe/!current-route)
                       params))))

(defn wheel-set-table [opts data]
  (let [mobile? (ui/use-mobile? 950)]
    [ui/simple-table
     (merge opts {:mobile? mobile?
                  :table (fn [row] (cond-> {:class ["mb-5 w-full rounded-md overflow-hidden ring ring-white ring-2 bg-white shadow-md"
                                                    (when mobile? "text-sm cursor-pointer hover:ring-indigo-400")]}
                                     mobile? (assoc :on-click #(navigate-to-workflow! {:workflow/type "wheel-set"
                                                                                       :ductile/id (:ductile/id row)}))))
                  :thead {:class (when-not mobile? "bg-slate-50")}
                  :th (fn [attr]
                        {:class
                         (ui/class-vec
                          "text-left font-normal text-[11px]"
                          (if mobile?
                            "p-1 w-1/3 "
                            ["p-2 border-b uppercase text-gray-500"
                             (case attr
                               :vehicle/vin "w-24"
                               nil)]))})
                  :tr {:class [(if mobile?
                                 "odd:bg-white even:bg-slate-50"
                                 "hover:bg-indigo-200/20 border-b last:border-b-0")]}
                  :td (fn [v row attr]
                        {:class [(when (and (= :vehicle/vin attr) (not mobile?)) "cursor-pointer text-indigo-700 hover:underline")
                                 (if mobile? "p-1" "p-2")]
                         :on-click (when (= :vehicle/vin attr) #(navigate-to-workflow! {:workflow/type "wheel-set"
                                                                                        :ductile/id (:ductile/id row)}))})})
     data]))

(defn wheel-set-service-list [{:keys [data]}]
  [:div.p-3
   (show-error-or
    data
    (fn [wheel-sets]
      [wheel-set-table {:schema [:vehicle/vin :shelf/row :shelf/place :shelf/level :user/email]} wheel-sets]))])

;; ===================
;; lonato-service-list
;; ===================

(defn group-load-view [group-key fahrzeuge]
  (r/with-let [!expanded? (r/atom false)]
    (let [{:keys [group-bg group-expanded-bg]} (color-defs :gray)]
      [:div
       [:div.mb-3.relative.lg:mb-0
        [:div.mx-auto.rounded.pl-1.pr-3.py-2.z-10.text-md.lg:w-full.lg:py-2.lg:rounded-none.lg:border-b.flex.items-center.justify-between.cursor-pointer
         {:class (str (if @!expanded? group-expanded-bg group-bg)
                      "lg:border-[rgba(0,0,0,.1)]")
          :on-click #(swap! !expanded? not)}
         [:div.text-black.mr-1.transition-all
          {:class (when @!expanded? "rotate-90")}
          [ui/icon {:size 16} :chevron-right]]
         [:div.w-full.flex.justify-between.items-center
          [:span.font-bold group-key]
          [:div.flex
           [:span.font-bold.border.border-white.px-2.rounded-full.shadow.flex.items-center
            {:class "py-[2px] bg-white bg-opacity-30"}
            [:span.mr-1 (count fahrzeuge)]
            [ui/icon {:size 16} :car]]]]]]
       (when @!expanded?
         (into [:div
                [:div.px-3.hidden.lg:flex.bg-white.py-1.border-b.bg-slate-50 {:class "text-[11px]"}
                 [:div.font-normal.uppercase.text-gray-500 {:class "lg:w-[200px]"} (tr "VIN")]
                 [:div.font-normal.uppercase.text-gray-500 {:class "lg:w-[250px]"} (tr "Type")]
                 [:div.font-normal.uppercase.text-gray-500 {:class "lg:w-[172px]"} (tr "Loading line")]
                 [:div.font-normal.uppercase.text-gray-500 {:class "lg:w-[172px]"} (tr "Load ID")]
                 [:div.font-normal.uppercase.text-gray-500 {:class "lg:w-[172px]"} (tr "Load preparable since")]
                 [:div.flex.justify-end.flex-auto
                  [:div.font-normal.uppercase.text-gray-500 {:class "ml-[38px]"} (tr "Zone / Row / Place")]]]]
               (map (fn [fahrzeug]
                      (let [loading-line (-> fahrzeug :exit/transport :transport/loading-line)]
                        [:div.mb-4.lg:mb-0.bg-white.rounded.lg:rounded-none.shadow.lg:shadow-none.border.lg:border-0.lg:border-b.flex.flex-col.lg:flex-row.lg:justify-between
                         {:on-click #(navigate-to-vin! (:vehicle/vin fahrzeug))}
                         [:div.flex.justify-between.p-3.items-center
                          [:div.lg:flex.items-center
                           (show-vin fahrzeug)
                           [:div.text-xs.lg:text-sm.pr-4
                            {:class "lg:w-[250px]"} (:vehicle/model fahrzeug)]]]
                         [:span.p-3.font-bold {:class "lg:w-[172px]"} (str (:parking-zone/name (:parking-spot/zone loading-line))
                                                                           (:parking-spot/row loading-line))]
                         [:span.p-3 {:class "lg:w-[172px]"} (-> fahrzeug :exit/transport :load/id)]
                         [:span.p-3 {:class "lg:w-[172px]"} (time-format/->str {:format :datetime :locale :it :style :short} (-> fahrzeug :exit/transport :transport/preparable-at))]
                         [:div.flex.p-3.border-t.lg:border-0.justify-between.lg:justify-end.items-center
                          [:div.mr-3
                           [:div.text-sm.font-bold.lg:font-normal.tracking-wider (parked-at-str fahrzeug)]]]]))
                    (sort-by (juxt :parking-zone/name :parking-spot/row :parking-spot/place :vehicle/vin)
                             fahrzeuge))))])))

;; =====================================
;; /gutachter (Bereitstellung Gutachter)
;; =====================================

(def gutachter-color-coding
  {:today         :green
   :tomorrow      :green
   :next-monday   :green
   :previous-week :green
   :next-week     :green
   :past          :red
   :future        :gray
   :unknown       :green})


(defn day-group [{:keys [color-coding termin show-inspection-company?]} vehicles & children]
  (r/with-let [relative-scheduled-date (:service/relative-scheduled-date (first vehicles))
               important-termin? (#{nil :today :tomorrow :next-monday} relative-scheduled-date)
               !expanded? (r/atom important-termin?)]
    (let [{:keys [group-bg termin-bg group-expanded-bg]} (get color-defs
                                                              (get color-coding
                                                                   relative-scheduled-date
                                                                   (:unknown color-coding)))]
      [:div
       [:div.mb-3.relative.lg:mb-0
        [:div.mx-auto.rounded.pl-1.pr-3.py-2.z-10.text-xs.lg:w-full.lg:py-1.lg:rounded-none.lg:border-b.flex.items-center.justify-between.cursor-pointer
         {:class (str (if @!expanded? group-expanded-bg group-bg)
                      "lg:border-[rgba(0,0,0,.1)]")
          :on-click #(swap! !expanded? not)}
         [:div.flex.items-center
          [:div.text-black.mr-1.transition-all
           {:class (when @!expanded? "rotate-90")}
           [ui/icon {:size 16} :chevron-right]]
          (if termin
            [:span.font-bold
             (when (= :today relative-scheduled-date) "Heute, ")
             (when (= :tomorrow relative-scheduled-date) "Morgen, ")
             (time-format/->str {:format :date :locale :de :style :full} termin)]
            [:span.font-bold "ohne Termindatum"])]
         (let [vehicle-count (count vehicles)]
           [:span.font-bold vehicle-count
            (if (< vehicle-count 2)
              [:span " Fahrzeug" [:span.opacity-0 "e"]]
              " Fahrzeuge")])]]
       (when @!expanded?
         (into [:<>] children))])))

(defn grouped-service-list [{:keys [localstorage-key grouping-key color-coding parked-label not-parked-label filterable-by show-inspection-company?]}
                            {:keys [data]}]
  (let [!filters (ui/use-localstorage (or localstorage-key ::grouped-service-list)
                                      {:parked-at-service-point? :all
                                       :inspection/company-name :all})]
    (show-error-or
     data
     (fn [value]
       (let [filter-preds (map (fn [[attr value]]
                                 (cond
                                   (= :all value) (constantly true)
                                   (not value) (comp not attr)
                                   :else (comp (partial = value) attr)))
                               @!filters)
             fahrzeuge (filter (apply every-pred filter-preds) value)]
         [:<>
          (into
           [:div.overflow-y-auto.flex-auto.p-4.bg-slate-100
            (when (and parked-label not-parked-label)
              (filters-dropdown
               {:list-size (count fahrzeuge)
                :data-testid :parked-at-service-point?
                :value (:parked-at-service-point? @!filters)
                :on-change (partial swap! !filters assoc :parked-at-service-point?)
                :options [{:value :all :label (tr "All vehicles")}
                          {:value true :label parked-label}
                          {:value false :label not-parked-label}]}))


            (into [:<>]
                  (map (fn [filterable]
                         (case filterable
                           :inspection/company-name
                           (filters-dropdown
                            {:list-size (count fahrzeuge)
                             :data-testid :inspection/company-name
                             :value (:inspection/company-name @!filters)
                             :on-change (partial swap! !filters assoc :inspection/company-name)
                             :options [{:value :all :label (tr "All inspection companies")}
                                       {:value "Macadam" :label "Macadam"}
                                       {:value "FSP" :label "FSP"}]})
                           nil)))
                  filterable-by)]

           (map (fn [[group-key vehicles]]
                  (let [{:keys [termin-bg]} (get color-defs
                                                 (get color-coding
                                                      (:service/relative-scheduled-date (first vehicles))
                                                      (:unknown color-coding)))
                        table [responsive-table
                               {:schema [:vehicle/vin
                                         :vehicle/model
                                         [:fahrzeug/expected-customer-delivery-datetime
                                          {:render (fn [v]
                                                     (when-let [scheduled-for v]
                                                       [:div.text-xs.font-bold.lg:font-normal.leading-5.lg:leading-tight.px-2.rounded-full.lg:ml-3.inline-flex
                                                        {:class (str termin-bg " lg:px-[8px] lg:py-[1px]")}
                                                        (time-format/->str {:format :date :locale :de} scheduled-for)]))}]
                                         :parking-spot
                                         (when (and show-inspection-company?
                                                    (= :all (:inspection/company-name @!filters)))
                                           :inspection/company-name)
                                         :fahrzeug/delivery-dealer-code-raw]}
                               vehicles]]
                    (if grouping-key
                      [day-group {:color-coding color-coding
                                  :group-key :group-key
                                  :show-inspection-company? (and show-inspection-company?
                                                                 (= :all (:inspection/company-name @!filters)))}
                       vehicles table]
                      table))))
           (->> fahrzeuge
                (group-by (or grouping-key (constantly nil)))
                (sort-by key util/compare-with-nil-last)))])))))

(def gutachter-service-list
  (partial grouped-service-list {:localstorage-key ::gutachter-service-list
                                 :parked-label "Autos die bereits bei der Gutachter Halle sind"
                                 :not-parked-label "Autos die noch zur Gutachter Halle gebracht werden müssen"
                                 :color-coding gutachter-color-coding
                                 :grouping-key :fahrzeug/expected-customer-delivery-datetime}))

(defn eitting-gutachter-service-list
  [{:keys [data]}]
  (let [localstorage-key ::eitting-gutachter-service-list
        parked-label "Autos, die bereits im Puffer Gutachter sind"
        not-parked-label "Autos, die zum Puffer Gutachter gebracht werden müssen"
        filterable-by #{:inspection/company-name}
        show-inspection-company? true
        color-coding gutachter-color-coding
        schema [:vehicle/vin
                :vehicle/model
                [:entry/datetime
                 {:render (fn [v]
                            (when-let [scheduled-for v]
                              [:div.text-xs.font-bold.lg:font-normal.leading-5.lg:leading-tight.px-2.rounded-full.lg:ml-3.inline-flex
                               {:class "lg:px-[8px] lg:py-[1px]"}
                               (time-format/->str {:format :date :locale :de} scheduled-for)]))}]
                :parking-spot
                :inspection/company-name]
        !filters (ui/use-localstorage (or localstorage-key ::grouped-service-list)
                                      {:parked-at-service-point? :all
                                       :inspection/company-name :all})]
    (show-error-or
     data
     (fn [value]
       (let [filter-preds (map (fn [[attr value]]
                                 (cond
                                   (= :all value) (constantly true)
                                   (not value) (comp not attr)
                                   :else (comp (partial = value) attr)))
                               @!filters)
             fahrzeuge (filter (apply every-pred filter-preds) value)]
         [:<>
          (into
           [:div.overflow-y-auto.flex-auto.p-4.bg-slate-100
            (when (and parked-label not-parked-label)
              (filters-dropdown
               {:list-size (count fahrzeuge)
                :value (:parked-at-service-point? @!filters)
                :on-change (partial swap! !filters assoc :parked-at-service-point?)
                :options [{:value :all :label (tr "All vehicles")}
                          {:value true :label parked-label}
                          {:value false :label not-parked-label}]}))


            (into
             [:div]
             (map (fn [filterable]
                    (case filterable
                      :inspection/company-name
                      (filters-dropdown
                       {:list-size (count fahrzeuge)
                        :value (:inspection/company-name @!filters)
                        :on-change (partial swap! !filters assoc :inspection/company-name)
                        :options [{:value :all :label (tr "All inspection companies")}
                                  {:value "Macadam" :label "Macadam"}
                                  {:value "FSP" :label "FSP"}]})
                      nil))
                  filterable-by))

            [responsive-table
             {:schema schema}
             (->> fahrzeuge
                  (sort-by :sort-key util/compare-with-nil-last))]])])))))

;; ==============================================
;; /lonato-loads (Spostamento in Linea di Carico)
;; /bring-to-storage (Spostamento in Storage)
;; ==============================================

(defn lonato-base-movements-service-list [query-result opts]
  (r/with-let [!grouping (r/atom (keyword (or (localstorage/get-item (:localstorage-key opts)) "by-parking")))]
    [:<>
     [:div.px-4.pt-4
      [:div.text-xs.font-bold.flex.items-center.w-full.gap-3.mb-3.-mt-1
       [:div.flex.items-center.gap-2
        [:div.flex-shrink-0 {:class "w-[80px] md:w-auto text-right"} (tr "Grouping")]
        (into [:select.select-box.w-full
               {:style {:font-size 12 :font-weight "bold"}
                :class "py-[8px] md:py-[5px]"
                :value @!grouping
                :on-change (fn [event]
                             (let [v (.. event -target -value)]
                               (reset! !grouping (keyword v))
                               (localstorage/set-item! (:localstorage-key opts) v)))}]
              (keep (fn [o] (when o
                              [:option o])))
              [(when (:by-loading-line? opts) {:value "by-loading-line" :label (tr "By loading-line")})
               {:value "by-parking-zone" :label (tr "By parking location")}])]]]
     (into
      [:div.overflow-y-auto.flex-auto.p-4.bg-slate-100
       (article-count query-result)]
      (case @!grouping
        :by-loading-line (map (fn [[zone fahrzeuge]]
                                [group-load-view zone fahrzeuge])
                              (->> query-result
                                   (group-by (comp loading-line-str :transport/loading-line :exit/transport))
                                   (sort-by key)))
        (map (fn [[zone fahrzeuge]]
               [group-load-view zone fahrzeuge])
             (->> query-result
                  (group-by :parking-zone/name)
                  (sort-by key)))))]))

(defn lonato-loads-service-list [{:keys [data]}]
  (lonato-base-movements-service-list data {:localstorage-key "load-service-list/grouping"
                                            :by-loading-line? true}))

(defn lonato-movements-service-list [{:keys [data]}]
  (lonato-base-movements-service-list data {:localstorage-key "movements-service-list/grouping"
                                            :by-loading-line? false}))


;; =======================
;; /transports (Trasporti)
;; =======================

(defn transport-view [{:transport/keys [loading-line fahrzeuge] :as transport}]
  (r/with-let [!expanded? (r/atom false)]
    (let [loading-line-str (str (:zone loading-line) (:row loading-line))
          parked-in-loading-line? (fn [fahrzeug]
                                    (and (= (:zone loading-line) (:parking-zone/name fahrzeug))
                                         (= (:row loading-line) (:parking-spot/row fahrzeug))))
          fahrzeuge-at-loading-line (filter parked-in-loading-line? fahrzeuge)
          {:keys [group-bg group-expanded-bg]} (color-defs :gray)
          completion (int (* 100 (/ (count fahrzeuge-at-loading-line)
                                    (count fahrzeuge))))
          percentage (str completion "%")]
      [:div
       [:div.mb-3.relative.lg:mb-0
        [:div.mx-auto.rounded.pl-1.pr-3.py-2.z-10.text-md.lg:w-full.lg:py-2.lg:rounded-none.lg:border-b.flex.items-center.justify-between.cursor-pointer
         {:class (str (if @!expanded? group-expanded-bg group-bg)
                      "lg:border-[rgba(0,0,0,.1)]")
          :on-click #(swap! !expanded? not)}
         [:div.flex.items-center {:class "w-1/6"}
          [:div.text-black.mr-1.transition-all
           {:class (str "w-1/6 "  (when @!expanded? "rotate-90"))}
           [ui/icon {:size 16} :chevron-right]]
          [:div {:class "w-5/6"}
           [:div
            [:span.font-bold (str "# " (:load/id transport))]]]]
         [:div {:class "w-1/4"}
          [:div
           [:span (time-format/->str {:format :date :locale :it} (:ductile/created-at transport))]]]
         [:div.mx-2.bg-white.rounded-full {:class "w-1/3"}
          (when (> completion 0)
            [:div.bg-blue-600.text-xs.font-medium.text-blue-100.text-center.leading-none.rounded-full {:class "py-0.5" :style {:width percentage}} percentage])]
         [:div.text-right.flex.items-center
          (when loading-line
            [:span.font-bold.mr-4 loading-line-str])
          [:div.text-black.flex.items-center.justify-center.flex-shrink-0.mr-3
           {:class "w-[48px] h-[24px]"}]
          [:div.flex.items-center.justify-end
           [:span.font-bold.border.border-white.px-2.rounded-full.shadow.flex.items-center
            {:class "py-[2px] bg-white bg-opacity-30"}
            [:span.mr-1 (count fahrzeuge-at-loading-line) "/" (count fahrzeuge)]
            [ui/icon {:size 16} :car]]]]]]
       (when @!expanded?
         [:div
          [:div.px-3.hidden.lg:flex.bg-white.py-1.border-b.bg-slate-50 {:class "text-[11px]"}
           [:div.font-normal.uppercase.text-gray-500 {:class "lg:w-[200px]"} (tr "VIN")]
           [:div.font-normal.uppercase.text-gray-500 {:class "lg:w-[250px]"} (tr "Type")]
           [:div.flex.justify-end.flex-auto
            [:div.font-normal.uppercase.text-gray-500 {:class "ml-[38px]"} (tr "Zone / Row / Place")]]]
          (for [fahrzeug (sort-by (juxt parked-at-str :vehicle/vin)
                                  fahrzeuge)
                :let [location-str (parked-at-str fahrzeug)]]
            ^{:key (:vehicle/vin fahrzeug)}
            [:div.mb-4.lg:mb-0.bg-white.rounded.lg:rounded-none.shadow.lg:shadow-none.border.lg:border-0.lg:border-b.flex.flex-col.lg:flex-row.lg:justify-between
             {:on-click #(navigate-to-vin! (:vehicle/vin fahrzeug))}
             [:div.flex.justify-between.p-3.items-center
              [:div.lg:flex.items-center
               {:data-testid "fahrzeug"}
               (show-vin fahrzeug)
               [:div.text-xs.lg:text-sm.pr-4
                {:class "lg:w-[250px]"} (:vehicle/model fahrzeug)]]]
             [:div.flex.p-3.border-t.lg:border-0.justify-between.lg:justify-end.items-center
              [:div.flex.items-center.gap-4.mr-3
               (keep (fn [service-code]
                       (when (not= "112281" (:fahrzeug/delivery-dealer-code-raw fahrzeug))
                         [:div.leading-none.p-1.border-2.border-slate-300.rounded-full.flex.bg-white {:class "bg-red-500"}
                          [:span.rounded-full.px-2.py-1.font-bold.text-white.whitespace-nowrap {:class ["text-[11px]" "bg-red-500"]} service-code]]))
                     (set/intersection #{"Lavaggio PDI" "PDI" "PDI - Inserimento tappetini"}
                                       (:workflow/open-service-codes fahrzeug)))]
              [:div.mr-3
               [:div.text-sm.font-bold.lg:font-normal.tracking-wider
                (let [bg-color (if (parked-in-loading-line? fahrzeug)
                                 "bg-green-500"
                                 "bg-red-500")]
                  [:div.leading-none.p-1.border-2.border-slate-300.rounded-full.flex.bg-white {:class bg-color}
                   [:span.rounded-full.px-2.py-1.font-bold.text-white.whitespace-nowrap {:class ["text-[11px]" bg-color]} location-str]])
                #_location-str]]]])])])))

(defn transport-key-fn [{:transport/keys [loading-line fahrzeuge] :as transport}]
  (as-> fahrzeuge %
    (filter #(= (:zone loading-line) (:parking-zone/name %)) %)
    (filter #(= (:row loading-line) (:parking-spot/row %)) %)
    (count %)
    (/ % (count fahrzeuge))
    (- %)))

(defn transports-service-list [{:keys [data]}]
  [:div.md:flex.items-center.flex-wrap
   [:div.px-4.pb-4.w-full
    (doall
     (for [transport (sort-by transport-key-fn data)]
       ^{:key (:ductile/id transport)}
       [transport-view transport]))]])


;; ==================
;; /loads (Ladeliste)
;; ==================

(defn parked-in-loading-line? [fahrzeug]
  (and (:parking-zone/name fahrzeug)
       (:parking-spot/row fahrzeug)
       (-> fahrzeug :exit/transport :transport/loading-line)
       (= (:parking-zone/name fahrzeug)
          (-> fahrzeug :exit/transport :transport/loading-line :parking-spot/zone :parking-zone/name))
       (= (:parking-spot/row fahrzeug)
          (-> fahrzeug :exit/transport :transport/loading-line :parking-spot/row))))

(defn load-priority [fahrzeug]
  [(:fahrzeug/expected-customer-delivery-datetime fahrzeug)
   (:tour fahrzeug)])

(defn zone-group [zone vehicles]
  (r/with-let [!expanded? (r/atom false)]
    (let [needs-keys? (some :needs-keys? vehicles)
          license-plate? (some :vehicle/license-plate vehicles)]
      [:div
       [:div.mb-3.relative.lg:mb-0
        [:div.mx-auto.rounded.pl-1.pr-3.py-2.z-10.text-xs.lg:w-full.lg:py-1.lg:rounded-none.lg:border-b.flex.items-center.justify-between.cursor-pointer
         {:class "bg-sky-200 lg:border-[rgba(0,0,0,.1)]"
          :on-click #(swap! !expanded? not)}
         [:div.flex.items-center
          [:div.text-black.mr-1.transition-all
           {:class (when @!expanded? "rotate-90")}
           [ui/icon {:size 16} :chevron-right]]
          [:div
           [:div
            [:span.font-bold zone]]]]
         [:div.text-right.flex.items-center
          [:div.text-black.flex.items-center.justify-center.flex-shrink-0.mr-3
           {:class "w-[48px] h-[24px]"}
           [:span.text-right.flex.items-center
            (str (when needs-keys? "🔑"))]]
          [:div.flex.items-center.justify-end
           (let [car-count (count vehicles)]
             [:span.font-bold.border.border-white.px-2.rounded-full.shadow.flex.items-center
              {:class "py-[2px] bg-white bg-opacity-30"}
              [:span.mr-1 car-count]
              [ui/icon {:size 16} :car]])]]]]
       (when @!expanded?
         [responsive-table
          {:schema [[:vehicle/vin {:render (fn [v] [:a.font-mono.cursor-pointer {:on-click #(navigate-to-vin! v) :href (vin-link v)} [ui/vin v]])}]
                    [:vehicle/model {:render (fn [v fahrzeug] [:span.flex.gap-2.items-center (:make/name fahrzeug) " " v [fahrzeug/package-badge fahrzeug]])}]
                    (when license-plate? :vehicle/license-plate)
                    [:unloading-city {:label (tr "Stops #")
                                      :render (fn [_ {:as fahrzeug :keys [unloading-city unloading-point]}]
                                                [:div unloading-city [:span.text-slate-500 " " (dec unloading-point)]])}]
                    [:transport/loading-line {:derive #(-> % :exit/transport :transport/loading-line)
                                              :render (fn [v] [:b (loading-line-str v)])}]
                    [:parking-spot/place {:label (tr "Zone / Row / Place")
                                          :derive (comp (partial remove nil?) (juxt :parking-zone/name :parking-spot/row :parking-spot/place))
                                          :render (fn [v {:as fahrzeug :keys [needs-keys?]}]
                                                    [:div.flex.items-center.tabular-nums.font-bold.whitespace-nowrap
                                                     (into [:<>] (interpose [:span.text-sm.mx-1.text-slate-400 "/"] v))
                                                     (when needs-keys? " 🔑")])}]
                    [:fahrzeug/delivery-dealer-code-raw {:label (tr "Dealer")
                                                         :render (fn [v] [:span.tabular-nums v])}]]}
          (sort-by (juxt parked-at-str :vehicle/vin)
                   vehicles)])])))

(defn load-group [tour vehicles]
  (r/with-let [!expanded? (r/atom false)]
    (let [tour-date (-> vehicles first :fahrzeug/expected-customer-delivery-datetime)
          in-progress-vehicles (filter parked-in-loading-line? vehicles)
          needs-keys? (some :needs-keys? vehicles)
          tour-status (cond
                        (= (count in-progress-vehicles) (count vehicles)) :done
                        (pos? (count in-progress-vehicles)) :in-progress
                        :else :not-started)
          end-destination (some :unloading-city vehicles)
          transport (-> vehicles first :exit/transport)
          loading-line (:transport/loading-line transport)
          truck-license-plate (or (:transport/license-plate transport)
                                  (-> transport :transport/transporter :transport/license-plate))
          truck-number (-> vehicles first :truck-number)
          license-plate? (some :vehicle/license-plate vehicles)
          unloading-line (-> in-progress-vehicles first :parking-spot/row)
          {:keys [group-bg group-expanded-bg]} ((comp color-defs {:done :green
                                                                  :in-progress :orange
                                                                  :not-started :red})
                                                tour-status)]
      [:div.tabular-nums
       [:div.mb-3.relative.lg:mb-0
        [:div.mx-auto.rounded.pl-1.pr-3.py-2.z-10.text-xs.lg:w-full.lg:py-1.lg:rounded-none.lg:border-b.flex.items-center.justify-between.cursor-pointer
         {:class (str (if @!expanded? group-expanded-bg group-bg)
                      "lg:border-[rgba(0,0,0,.1)]")
          :on-click #(swap! !expanded? not)}
         [:div.flex.items-center
          [:div.text-black.mr-1.transition-all
           {:class (when @!expanded? "rotate-90")}
           [ui/icon {:size 16} :chevron-right]]
          [:div
           [:div

            [:span.font-bold (str (tr "Transport") " "
                                  tour
                                  (when (:transport/name transport)
                                    (str " (" (:transport/name transport) ")")))]
            [:span " " (tr "to") " "]
            [:span.font-bold end-destination]
            [:span.font-bold
             (when truck-number
               (str " ("
                    (if (= "SA" truck-number)
                      (tr "self-pickup")
                      (str (tr "Truck") " " truck-number))
                    (when truck-license-plate
                      (str " - " truck-license-plate))
                    ")"))]]
           [:div
            [:span {:class "text-[11px]"}
             (time-format/->str {:format :date :style :full} tour-date)]]
           (when unloading-line
             [:div.md:hidden {:class "text-[11px]"}
              [:span "geparkt in "]
              [:span.font-bold.mr-4 (str (tr "Row") " # " unloading-line)]])]]
         [:div.text-right.flex.items-center
          (when loading-line
            [:span.hidden.md:inline
             [:span (str (tr "Loading line") " ")]
             [:span.font-bold.mr-4 (loading-line-str loading-line)]])
          (when (and unloading-line
                     (not loading-line))
            [:span.hidden.md:inline
             [:span (tr "Parked at") " "]
             [:span.font-bold.mr-2 (str (tr "Row") " # " unloading-line)]])
          [:div.text-black.flex.items-center.justify-center.flex-shrink-0.mr-3
           {:class "w-[48px] h-[24px]"}
           [:span.text-right.flex.items-center
            (str (when needs-keys? "🔑"))]]
          [:div.flex.items-center.justify-end
           (let [car-count (count vehicles)]
             [:span.font-bold.border.border-white.px-2.rounded-full.shadow.flex.items-center
              {:class "py-[2px] bg-white bg-opacity-30"}
              [:span.mr-1 (count in-progress-vehicles) "/" car-count]
              [ui/icon {:size 16} :car]])]]]]
       (when @!expanded?
         [responsive-table
          {:schema [[:vehicle/vin {:render (fn [v] [:a.font-mono.cursor-pointer {:on-click #(navigate-to-vin! v) :href (vin-link v)} [ui/vin v]])}]
                    [:vehicle/model {:render (fn [v fahrzeug] [:span.flex.gap-2.items-center (:make/name fahrzeug) " " v [fahrzeug/package-badge fahrzeug]])}]
                    (when license-plate? :vehicle/license-plate)
                    [:unloading-city {:label (tr "Stops #")
                                      :render (fn [_ {:as fahrzeug :keys [unloading-city unloading-point]}]
                                                [:div unloading-city])}]
                    [:transport/loading-line {:derive #(-> % :exit/transport :transport/loading-line)
                                              :render (fn [v] [:b (loading-line-str v)])}]
                    [:parking-spot/place {:label (tr "Zone / Row / Place")
                                          :derive (comp (partial remove nil?) (juxt :parking-zone/name :parking-spot/row :parking-spot/place))
                                          :render (fn [v {:as fahrzeug :keys [needs-keys?]}]
                                                    [:div.flex.items-center.tabular-nums.font-bold.whitespace-nowrap
                                                     (into [:<>] (interpose [:span.text-sm.mx-1.text-slate-400 "/"] v))
                                                     (when needs-keys? " 🔑")])}]
                    [:fahrzeug/delivery-dealer-code-raw {:label (tr "Dealer")
                                                         :render (fn [v] [:span.tabular-nums v])}]]}
          (sort-by (juxt parked-at-str :vehicle/vin)
                   vehicles)])])))

;; TODO: Uses flex-wrap as a hack to prevent binding the tours vars.
;; All of this here needs to be rebuild with deframe in a more composable way.
(defn loads-service-list [{:keys [data]}]
  (r/with-let [!selected-filter (r/atom "need-preparation")
               !grouping        (r/atom (or (js/window.localStorage.getItem "loads-service-list/grouping") "by-transport"))]
    [:<>
     [:div.md:flex.items-center.flex-wrap
      [:div.px-4.pt-4
       [:div.text-xs.font-bold.flex.items-center.w-full.gap-3.mb-3.-mt-1
        [:div.flex.items-center.gap-2
         [:div.flex-shrink-0 {:class "w-[80px] md:w-auto text-right"} (tr "Grouping")]
         [:select.select-box.w-full
          {:style {:font-size 12 :font-weight "bold"}
           :class "py-[8px] md:py-[5px]"
           :value @!grouping
           :on-change (fn [event]
                        (let [v (-> event .-target .-value)]
                          (reset! !grouping v)
                          (js/window.localStorage.setItem "loads-service-list/grouping" v)))}
          [:option {:value "by-transport" :label (tr "By transport")}]
          [:option {:value "by-parking-zone" :label (tr "By parking location")}]]]]]
      (case @!grouping
        "by-parking-zone"
        (into
         [:div.px-4.pb-4.w-full]
         (map (fn [[group-key vehicles]]
                ^{:key (str "zone-group-" group-key)}
                [zone-group group-key vehicles]))
         (->> (remove parked-in-loading-line? data)
              (group-by :parking-zone/name)))

        ;; "by-transport" and fallback
        (let [tours' (sort-by (fn [[_tour vehicles]] (->> vehicles first load-priority))
                              (group-by :tour data))
              tours (case @!selected-filter
                      "all" tours'
                      "need-preparation"
                      (->> tours'
                           (filter (fn [[_tour vehicles]] (every? :can-be-prepared? vehicles)))
                           (remove (fn [[_tour vehicles]] (every? parked-in-loading-line? vehicles)))))]
          [:<>
           [:div.px-4.md:pt-4
            (filters-dropdown
             {:list-size (count tours)
              :value     @!selected-filter
              :on-change (partial reset! !selected-filter)
              :options [{:value "all" :label (tr "All vehicles")}
                        {:value "need-preparation" :label (tr "Vehicles that need to be brough to a loading line")}]
              :label [:div.flex-shrink-0 {:class "w-[80px] md:w-auto text-right"} (tr "Show") ":"]})]
           [:div.px-4.pb-4.w-full
            (doall
             (for [[group-key vehicles] tours]
               ^{:key (str "load-group-" group-key)}
               [load-group group-key vehicles]))]]))]]))


;; =======
;; Page UI
;; =======

(def list-ui-symbol->view-fn
  (into {}
        (comp (filter (comp #(str/ends-with? % "-list") name key))
              (map (fn [[k v]] [(symbol "ductile.ui.service-list" (str k)) @v])))
        (ns-publics 'ductile.ui.service-list)))


(defn service-list [{:keys [service-list-type title ui error error-message items]
                     compound-name :compound/name
                     :as params}]
  (let [key {:service-list-type service-list-type
             :compound/name     compound-name}]
    (cond
      error-message
      (throw (ex-info error-message params))

      :else
      [mobile/scroll-content
       [mobile/navbar {:back [mobile/back-link {:route (router/path '[compound-ops :compound-name]
                                                                    {:compound-name compound-name})}]
                       :title title
                       :subtitle (article-count items)}]
       (if-let [ui-view (get list-ui-symbol->view-fn (:view ui))]
         [ui-view (merge params ui {:data items})]
         [error (str "Could not find list ui symbol for `" ui "`. Does it end in `-list`?")])])))
